import React from "react";
import { connect } from "react-redux";
import {
  serviceCaller,
  ServiceCallerProps,
  withLoginRedirect,
} from "../../composition";
import {
  Form,
  FormGroup,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Button,
} from "reactstrap";
import NumberFormat from "react-number-format";
import { Range } from "rc-slider";
import sliderCss from "rc-slider/assets/index.css";
import get from "lodash.get";
import BaseInput from "../../../toolkit/baseInput";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import classNames from "classnames";
import ISegment, { ISegmentParameter } from "../../../types/services/ISegment";
import { firebaseConnect } from "react-redux-firebase";
import Reform from "@franleplant/reform";
import * as commonActions from "../../../utils/actions/common";
import { condenseWhiteSpaces } from "../../../utils/commonFunctions";
import {
  isCurrentUserCollaborator,
  isCurrentUserOnlyCollaborator,
} from "../../../utils/roleCheck";
import firebase from "firebase/compat/app";
import { deleteSegment } from "../../../utils/fetch";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import { isOrdinaryDonorList } from "../thankYouAdmins/helpers";

import { selectUsesGiveSpark } from "../../../utils/giveSpark";
interface Props extends ServiceCallerProps {
  usesGiveSpark: boolean;
  allSegments: { [key: string]: ISegment }[];
  existingSegment: ISegment;
  firebase: firebase.app.App;
  hideSelection: () => void;
  refreshDashboard: () => void;
}

interface State {
  fields: {
    name: string;
    description: string;
  };
  errors: object;
  isDonorList: boolean;
  recency: ISegmentParameter;
  frequency: ISegmentParameter;
  amount: ISegmentParameter;
  formValid: boolean;
  nameError: boolean;
  donorListId: string;
  amountErrors: {
    maxValue?: string;
    minValue?: string;
  };
  busyLoading: "save" | "delete" | null;
}

const minRecency = 0;
const maxRecency = 60;

const minGift = 1;
const maxGift = 100;

const minAmount = 0.5;
const maxAmount = 99999999.99;

const rangeActiveColor = "#1F65B1";
const rangeInactiveColor = "#1F65B166";

class SegmentDetails extends React.PureComponent<Props, State> {
  re = Reform.reactMixins.objectMixin(this);

  validationRules = {
    name: { required: true, maxLength: 100 },
    description: { maxLength: 1000 },
  };

  validationMessages = {
    required: () => `Required`,
    maxLength: ruleValue => `Must be less than ${ruleValue} characters long`,
    default: () => `Invalid`,
  };

  state = {
    fields: {
      name: "",
      description: "",
    },
    isDonorList: true,
    donorListId: undefined,
    recency: null,
    frequency: null,
    amount: null,
    errors: {},
    nameError: false,
    formValid: false,
    amountErrors: {},
    busyLoading: null,
  };

  onValidation = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(
        state => {
          const fields = {
            ...state.fields,
            [fieldName]: value,
          };
          this.re.validateField(fieldName, value);
          return { ...state, error: {}, fields };
        },
        () =>
          this.setState({
            formValid: this.re.formIsValid(),
          })
      );
    };
  };

  filterChanged = () => {
    const newValue = !this.state.isDonorList;
    if (
      this.props.existingSegment &&
      this.props.existingSegment.isDonorList !== newValue
    ) {
      if (
        confirm(
          newValue
            ? "Removing filters will remove all donors from this segment. Proceed?"
            : "Adding filters will remove and replace all donors from this segment. Proceed?"
        )
      ) {
        this.setState({
          isDonorList: newValue,
        });
      }
    } else {
      this.setState({
        isDonorList: newValue,
      });
    }
  };

  filterIsFulfilled = (): boolean => {
    if (this.state.isDonorList) {
      return true;
    }
    return (
      this.state.frequency !== null ||
      this.state.recency !== null ||
      this.state.amount !== null
    );
  };

  recencyChanged = value => {
    this.setState({
      recency: { minValue: value[0], maxValue: value[1] },
    });
  };

  clearRecency = () => {
    this.setState(
      {
        recency: {
          minValue: (maxRecency - minRecency) / 4,
          maxValue: ((maxRecency - minRecency) * 3) / 4,
        },
      },
      () => {
        this.setState({
          recency: null,
        });
      }
    );
  };

  frequencyChanged = value => {
    this.setState({
      frequency: { minValue: value[0], maxValue: value[1] },
    });
  };

  minAmountChanged = values => {
    const value = values.floatValue;
    const minError = `Minimum must be higher than ${minAmount}`;
    this.setState({
      amount: {
        ...(this.state.amount ? this.state.amount : {}),
        minValue: values.floatValue,
      },
      amountErrors: {
        ...this.state.amountErrors,
        minValue: value < minAmount ? minError : undefined,
      },
    });
  };

  maxAmountChanged = values => {
    const value = values.floatValue;
    const maxError = `Maximum must be lower than ${maxAmount}`;
    this.setState({
      amount: {
        ...(this.state.amount ? this.state.amount : {}),
        maxValue: values.floatValue,
      },
      amountErrors: {
        ...this.state.amountErrors,
        maxValue: value > maxAmount ? maxError : undefined,
      },
    });
  };

  clearFrequency = () => {
    //First setState seems to be necessary due to a bug in range where setting to null does not reset position
    this.setState(
      {
        frequency: {
          minValue: (maxGift - minGift) / 4,
          maxValue: ((maxGift - minGift) * 3) / 4,
        },
      },
      () => {
        this.setState({
          frequency: null,
        });
      }
    );
  };

  clearAmount = () => {
    this.setState({
      amount: undefined,
    });
  };

  clearData = () => {
    this.setState(
      {
        recency: {
          minValue: (maxRecency - minRecency) / 4,
          maxValue: ((maxRecency - minRecency) * 3) / 4,
        },
        frequency: {
          minValue: (maxGift - minGift) / 4,
          maxValue: ((maxGift - minGift) * 3) / 4,
        },
      },
      () => {
        this.setState({
          recency: undefined,
          frequency: undefined,
          amount: undefined,
        });
      }
    );
  };

  cancel = () => this.props.navigate("/segments");

  deleteSegment = () => {
    if (confirm("Are you sure you want to delete this segment?")) {
      this.setState(
        {
          busyLoading: "delete",
        },
        () => {
          deleteSegment(
            this.props.currentCharity.id,
            this.props.params.segmentId,
            this.props.config.adminServiceUrl,
            this.props.serviceAccessToken
          )
            .then(() => {
              this.props.navigate("/segments");
            })
            .catch(error => {
              console.error("Error while deleting segment", error);
              this.setState({
                busyLoading: null,
              });
            });
        }
      );
    }
  };

  goToSegmentList = () => {
    this.props.navigate("/segments");
    setTimeout(
      function() {
        this.props.refreshDashboard();
      }.bind(this),
      1000
    );
  };

  deleteDonorFlag = listId => {
    this.setState(
      {
        busyLoading: "delete",
      },
      () => {
        this.props.firebase
          .database()
          .ref(this.props.currentCharity.id)
          .child("donorsFlags")
          .child(listId)
          .set(null)
          .then(() => {
            this.goToSegmentList();
          })
          .catch(error => {
            console.error("Error while deleting donor flag", error);
            this.setState({
              busyLoading: null,
            });
          });
      }
    );
  };

  saveSegment = () => {
    if (this.props.allSegments) {
      if (
        this.props.allSegments.some(segmentObject => {
          return (
            condenseWhiteSpaces(segmentObject.value.name.toUpperCase()) ===
              condenseWhiteSpaces(this.state.fields.name.toUpperCase()) &&
            (!this.props.params.segmentId ||
              this.props.params.segmentId !== segmentObject.key.id)
          );
        })
      ) {
        this.setState({
          nameError: true,
        });
        return;
      }
    }
    let baseRef = this.props.firebase
      .database()
      .ref(this.props.currentCharity.id)
      .child("segments");
    const finalRef = this.props.params.segmentId
      ? baseRef.child(this.props.params.segmentId)
      : baseRef.push();

    const donorListId = this.state.donorListId
      ? this.state.donorListId
      : finalRef.key;

    this.setState(
      {
        busyLoading: "save",
      },
      () => {
        const previousIsDonorList =
          this.props.existingSegment && this.props.existingSegment.isDonorList;
        const previousListId =
          this.props.existingSegment && this.props.existingSegment.donorListId;
        finalRef
          .update({
            ...(this.props.existingSegment &&
            this.props.existingSegment.isDonorList !== this.state.isDonorList
              ? { donorCount: 0 }
              : {}),
            isDonorList:
              typeof this.state.isDonorList !== "undefined"
                ? this.state.isDonorList
                : null,
            donorListId: this.state.isDonorList ? donorListId : null,
            name: this.state.fields.name,
            description: this.state.fields.description || null,
            recency:
              this.state.recency && !this.state.isDonorList
                ? {
                    minValue:
                      this.state.recency.minValue &&
                      this.state.recency.minValue > minRecency
                        ? this.state.recency.minValue * 30
                        : null,
                    maxValue:
                      this.state.recency.maxValue &&
                      this.state.recency.maxValue < maxRecency
                        ? this.state.recency.maxValue * 30
                        : null,
                  }
                : null,
            frequency:
              this.state.frequency && !this.state.isDonorList
                ? {
                    minValue:
                      this.state.frequency.minValue &&
                      this.state.frequency.minValue > minGift
                        ? this.state.frequency.minValue
                        : null,
                    maxValue:
                      this.state.frequency.maxValue &&
                      this.state.frequency.maxValue < maxGift
                        ? this.state.frequency.maxValue
                        : null,
                  }
                : null,
            amount:
              this.state.amount && !this.state.isDonorList
                ? {
                    minValue: this.state.amount.minValue
                      ? this.state.amount.minValue * 100
                      : null,
                    maxValue: this.state.amount.maxValue
                      ? this.state.amount.maxValue * 100
                      : null,
                  }
                : null,
          })
          .then(
            () => {
              if (previousIsDonorList && !this.state.isDonorList) {
                this.deleteDonorFlag(previousListId);
              } else {
                this.goToSegmentList();
              }
            },
            error => {
              console.error("Error while saving segment.", error);
              this.setState({ busyLoading: null });
            }
          );
      }
    );
  };

  static monthToString(month: number) {
    switch (month) {
      case 1:
        return "1 month";
      case maxRecency:
        return `${maxRecency}+ months`;
      default:
        return `${month} months`;
    }
  }

  static giftToString(gift: number) {
    switch (gift) {
      case 1:
        return "1 gift";
      case maxGift:
        return `${maxGift} gifts`;
      default:
        return `${gift} gifts`;
    }
  }

  componentDidMount() {
    this.props.hideSelection();
    if (window) {
      window.scrollTo(0, 0);
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.existingSegment !== prevProps.existingSegment &&
      this.props.existingSegment
    ) {
      //Populate fields
      const recencyMin = get(this.props.existingSegment, "recency.minValue");
      const recencyMax = get(this.props.existingSegment, "recency.maxValue");

      const frequencyMin = get(
        this.props.existingSegment,
        "frequency.minValue"
      );
      const frequencyMax = get(
        this.props.existingSegment,
        "frequency.maxValue"
      );

      const amountMin = get(this.props.existingSegment, "amount.minValue");
      const amountMax = get(this.props.existingSegment, "amount.maxValue");

      this.setState({
        fields: {
          name: this.props.existingSegment.name,
          description: this.props.existingSegment.description,
        },
        isDonorList: this.props.existingSegment.isDonorList,
        donorListId: this.props.existingSegment.donorListId,
        recency: this.props.existingSegment.recency
          ? {
              minValue: recencyMin ? recencyMin / 30 : minRecency,
              maxValue: recencyMax ? recencyMax / 30 : maxRecency,
            }
          : undefined,
        frequency: this.props.existingSegment.frequency
          ? {
              minValue: frequencyMin ? frequencyMin : minGift,
              maxValue: frequencyMax ? frequencyMax : maxGift,
            }
          : undefined,
        amount: this.props.existingSegment.amount
          ? {
              minValue: amountMin ? amountMin / 100 : null,
              maxValue: amountMax ? amountMax / 100 : null,
            }
          : undefined,
        formValid: true,
      });
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
    }
  }

  disabledInput = props => {
    return (
      <Input
        disabled={
          this.state.busyLoading != null ||
          !isCurrentUserCollaborator(this.props.currentCharity)
        }
        {...props}
      />
    );
  };

  preventSubmit = e => {
    e.preventDefault();
  };

  clearUniqueNameError = () => {
    this.setState({
      nameError: false,
    });
  };

  goToDonorList = () => {
    this.props.navigate(`contacts/segment`);
  };

  goToThankYouAdmins = () => {
    this.props.navigate(
      `/segments/${this.props.params.segmentId}/thank-you-admins`
    );
  };

  render() {
    const editable =
      isCurrentUserCollaborator(this.props.currentCharity) &&
      !get(this.props.existingSegment, "uneditable", false);
    return (
      <main className={s.main}>
        <div className={s.container}>
            <Row>
              <Col md={{ size: 8, offset: 2 }} sm={12} className={s.titleCol}>
                <div className={s.titleRow}>
                  <h2>
                    {this.props.params.segmentId
                      ? "Edit Segment"
                      : "New Segment"}
                  </h2>
                  {this.props.existingSegment && (
                    <div className={s.listActionsCol}>
                      <Button
                        outline
                        className={s.formToolbarSecondary}
                        onClick={this.goToDonorList}
                        disabled={this.state.busyLoading != null}
                      >
                        Contact List
                      </Button>
                      {isOrdinaryDonorList(
                        this.props.existingSegment || {
                          isDonorList: this.state.isDonorList,
                        }
                      ) && (
                        <Button
                          outline
                          className={s.formToolbarSecondary}
                          onClick={this.goToThankYouAdmins}
                          disabled={
                            this.state.busyLoading != null ||
                            !this.props.usesGiveSpark
                          }
                        >
                          Thank You Users
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={{ size: 8, offset: 2 }} sm={12}>
                <Form onSubmit={this.preventSubmit}>
                  <Row>
                    <Col xs="12">
                      <FormGroup>
                        <label className={s.label} htmlFor="segmentNameLabel">
                          * Name
                        </label>
                        <BaseInput
                          name="segmentNameInput"
                          id="segmentNameInput"
                          onChange={this.onValidation("name")}
                          onFocus={this.clearUniqueNameError}
                          value={this.state.fields.name}
                          disabled={
                            this.state.busyLoading != null || !editable
                          }
                        />
                      </FormGroup>
                      <ul>
                        {this.re
                          .mapFieldErrors("name")
                          .map((message, index) => {
                            return (
                              <li key={index} className={s.error}>
                                {message}
                              </li>
                            );
                          })}
                        {this.state.nameError && (
                          <li className={s.error}>
                            Segment name must be unique
                          </li>
                        )}
                      </ul>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs="12">
                      <FormGroup>
                        <label
                          className={s.label}
                          htmlFor="segmentDescriptionLabel"
                        >
                          Description
                        </label>
                        <BaseInput
                          disabled={this.state.busyLoading != null || !editable}
                          id={"segmentDescriptionText"}
                          name={"segmentDescriptionText"}
                          dataElmId={"segmentDescriptionText"}
                          type="textarea"
                          customClassName={s.textArea}
                          onChange={this.onValidation("description")}
                          value={this.state.fields.description}
                        />
                      </FormGroup>
                      <ul>
                        {this.re
                          .mapFieldErrors("description")
                          .map((message, index) => {
                            return (
                              <li key={index} className={s.error}>
                                {message}
                              </li>
                            );
                          })}
                      </ul>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} className={s.addFiltersCol}>
                      <div className={s.addFilters}>
                        <span className={s.label}>Add Filters</span>
                        <label className={s.switch}>
                          <input
                            type="checkbox"
                            onChange={this.filterChanged}
                            checked={!this.state.isDonorList}
                            disabled={!editable}
                          />
                          <span className={classNames(s.slider, s.round)} />
                        </label>
                      </div>
                    </Col>
                  </Row>
                  {!this.state.isDonorList && (
                    <div>
                      <Row>
                        <Col xs={12}>
                          <hr />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className={s.paramTitle}>Recency</div>
                          <div className={s.paramDescription}>
                            Number of months since last donation
                          </div>
                        </Col>
                        <Col sm={6} className={s.range}>
                          {this.state.recency && (
                            <span className={s.min}>
                              {SegmentDetails.monthToString(
                                this.state.recency.minValue
                              )}
                            </span>
                          )}
                          {this.state.recency && (
                            <span className={s.max}>
                              {SegmentDetails.monthToString(
                                this.state.recency.maxValue
                              )}
                            </span>
                          )}
                          {!this.state.recency && (
                            <span className={s.min}>&nbsp;</span>
                          )}
                          <Range
                            disabled={
                              this.state.busyLoading != null || !editable
                            }
                            className={s.rangePicker}
                            trackStyle={
                              this.state.recency
                                ? [
                                    { backgroundColor: rangeActiveColor },
                                    { backgroundColor: rangeActiveColor },
                                  ]
                                : [
                                    { backgroundColor: rangeInactiveColor },
                                    { backgroundColor: rangeInactiveColor },
                                  ]
                            }
                            handleStyle={
                              this.state.recency
                                ? [
                                    { borderColor: rangeActiveColor },
                                    { borderColor: rangeActiveColor },
                                  ]
                                : [
                                    { borderColor: rangeInactiveColor },
                                    { borderColor: rangeInactiveColor },
                                  ]
                            }
                            allowCross={false}
                            min={minRecency}
                            max={maxRecency}
                            defaultValue={[
                              (maxRecency - minRecency) / 4,
                              ((maxRecency - minRecency) * 3) / 4,
                            ]}
                            value={
                              this.state.recency
                                ? [
                                    this.state.recency.minValue,
                                    this.state.recency.maxValue,
                                  ]
                                : undefined
                            }
                            onChange={this.recencyChanged}
                          />
                          {!this.state.recency && editable && (
                            <div className={s.pickerText}>
                              Adjust slider to set recency
                            </div>
                          )}
                          {this.state.recency && editable && (
                            <div
                              onClick={this.clearRecency}
                              className={s.clearText}
                            >
                              Clear
                            </div>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <hr />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className={s.paramTitle}>Frequency</div>
                          <div className={s.paramDescription}>
                            Donations in the{" "}
                            {this.state.recency
                              ? "given month range"
                              : "last 12 months"}
                          </div>
                        </Col>
                        <Col sm={6} className={s.range}>
                          {this.state.frequency && (
                            <span className={s.min}>
                              {SegmentDetails.giftToString(
                                this.state.frequency.minValue
                              )}
                            </span>
                          )}
                          {this.state.frequency && (
                            <span className={s.max}>
                              {SegmentDetails.giftToString(
                                this.state.frequency.maxValue
                              )}
                            </span>
                          )}
                          {!this.state.frequency && (
                            <span className={s.min}>&nbsp;</span>
                          )}
                          <Range
                            disabled={
                              this.state.busyLoading != null || !editable
                            }
                            trackStyle={
                              this.state.frequency
                                ? [
                                    { backgroundColor: rangeActiveColor },
                                    { backgroundColor: rangeActiveColor },
                                  ]
                                : [
                                    { backgroundColor: rangeInactiveColor },
                                    { backgroundColor: rangeInactiveColor },
                                  ]
                            }
                            handleStyle={
                              this.state.frequency
                                ? [
                                    { borderColor: rangeActiveColor },
                                    { borderColor: rangeActiveColor },
                                  ]
                                : [
                                    { borderColor: rangeInactiveColor },
                                    { borderColor: rangeInactiveColor },
                                  ]
                            }
                            className={s.rangePicker}
                            allowCross={false}
                            min={minGift}
                            max={maxGift}
                            defaultValue={[
                              (maxGift - minGift) / 4,
                              ((maxGift - minGift) * 3) / 4,
                            ]}
                            value={
                              this.state.frequency
                                ? [
                                    this.state.frequency.minValue,
                                    this.state.frequency.maxValue,
                                  ]
                                : undefined
                            }
                            onChange={this.frequencyChanged}
                          />
                          {!this.state.frequency && editable && (
                            <div className={s.pickerText}>
                              Adjust slider to set frequency
                            </div>
                          )}
                          {this.state.frequency && editable && (
                            <div
                              onClick={this.clearFrequency}
                              className={s.clearText}
                            >
                              Clear
                            </div>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <hr />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className={s.paramTitle}>Amount</div>
                          <div className={s.paramDescription}>
                            Amount of last donation
                          </div>
                        </Col>
                        <Col sm={6}>
                          <InputGroup disabled={!editable}>
                            <NumberFormat
                              customInput={this.disabledInput}
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              thousandSeparator
                              prefix="$ "
                              placeholder="Lowest Amount"
                              onValueChange={this.minAmountChanged}
                              disabled={!editable}
                              value={
                                this.state.amount
                                  ? this.state.amount.minValue
                                  : null
                              }
                            />
                            <InputGroupText
                              className={s.squareBorders}
                            >
                              to
                            </InputGroupText>
                            <NumberFormat
                              customInput={this.disabledInput}
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              thousandSeparator
                              prefix="$ "
                              placeholder="Highest Amount"
                              onValueChange={this.maxAmountChanged}
                              disabled={!editable}
                              value={
                                this.state.amount
                                  ? this.state.amount.maxValue
                                  : null
                              }
                            />
                          </InputGroup>
                          {this.state.amount && editable && (
                            <div
                              onClick={this.clearAmount}
                              className={s.clearText}
                            >
                              Clear
                            </div>
                          )}
                          <ul className={s.error}>
                            {Object.values(this.state.amountErrors)
                              .filter(error => error)
                              .map((error, i) => (
                                <li key={i} className={s.errorLi}>
                                  {error as string}
                                </li>
                              ))}
                          </ul>
                        </Col>
                      </Row>
                    </div>
                  )}
                  <Row>
                    <Col xs={12} className={s.formToolbarCol}>
                      <div className={s.formToolbarBar}>
                        <div className={s.formToolbarCancelWrap}>
                          <Button
                            color="link"
                            className={s.formToolbarCancel}
                            onClick={this.cancel}
                            disabled={this.state.busyLoading != null}
                          >
                            {editable ? "Cancel" : "Back"}
                          </Button>
                        </div>
                        <div className={s.formToolbarActionGroup}>
                          {editable && !this.state.isDonorList && (
                            <Button
                              outline
                              className={s.formToolbarSecondary}
                              onClick={this.clearData}
                              disabled={this.state.busyLoading != null}
                            >
                              Clear Filters
                            </Button>
                          )}
                          {editable && this.props.existingSegment && (
                            <Button
                              outline
                              className={s.formToolbarSecondary}
                              onClick={this.deleteSegment}
                              disabled={this.state.busyLoading != null}
                            >
                              {this.state.busyLoading === "delete" ? (
                                <i className="fa fa-spinner fa-spin">&nbsp;</i>
                              ) : (
                                "Delete Segment"
                              )}
                            </Button>
                          )}
                          {editable && (
                            <Button
                              color="primary"
                              className={s.formToolbarPrimary}
                              onClick={this.saveSegment}
                              disabled={
                                !this.state.formValid ||
                                !this.props.currentCharity.id ||
                                Object.values(this.state.amountErrors).some(
                                  a => !!a
                                ) ||
                                this.state.busyLoading != null ||
                                !this.filterIsFulfilled() ||
                                this.state.nameError
                              }
                            >
                              {this.state.busyLoading === "save" ? (
                                <i className="fa fa-spinner fa-spin">&nbsp;</i>
                              ) : (
                                "Save Segment"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { firebase, user } = state;
  const previousSegment = get(
    firebase,
    `data.${user.currentCharity.id}.segments.${props.params?.segmentId}`
  );

  const segments = !get(firebase, `data.${user.currentCharity.id}.segments`)
    ? null
    : get(firebase, `ordered.${user.currentCharity.id}.segments`);

  return {
    usesGiveSpark: selectUsesGiveSpark(state),
    existingSegment: previousSegment,
    userId: firebase.auth.uid,
    allSegments: segments,
  };
};

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
});

const connected = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  serviceCaller(
    withLoginRedirect(
      firebaseConnect((props, store) => {
        const charityId = store.getState().user.currentCharity.id;
        const segmentId = props.params.segmentId;
        if (charityId && segmentId) {
          return [
            `${charityId}/segments`,
            `${charityId}/segments/${segmentId}`,
          ];
        } else if (charityId) {
          return [`${charityId}/segments`];
        } else {
          return [];
        }
      })(withStyles(s, sliderCss)(SegmentDetails))
    )
  )
);

export default withRouter(connected);
